/**
 * errorMessages.ts
 *
 * Maps raw technical error messages to customer-friendly messages.
 * Use getFriendlyError() or showPersistentError() instead of
 * passing raw error.message directly to users.
 *
 * IMPORTANT: ApiValidationError instances bypass this mapping entirely —
 * their messages come from the backend and are already user-facing.
 */
import toast from 'react-hot-toast';
import { useErrorStore, generateReferenceId } from '../store/errorStore';
import { captureMessage } from './sentry';
import { trackAppTrace, SeverityLevel } from './appInsights';
import { ApiValidationError, SystemError } from './errors';

export interface FriendlyError {
  title: string;
  description: string;
  /** Optional action label for the user to take */
  action?: string;
}

// Exact-string matches (checked first, highest priority)
const EXACT_MESSAGES: Record<string, FriendlyError> = {
  'Network Error': {
    title: 'Connection Problem',
    description: 'Unable to reach the server. Please check your internet connection and try again.',
    action: 'Check your connection',
  },
  'Failed to fetch': {
    title: 'Connection Problem',
    description: 'Unable to reach the server. Please check your internet connection and try again.',
    action: 'Check your connection',
  },
  'Request timeout': {
    title: 'Request Timed Out',
    description: 'The server is taking too long to respond. Please try again in a moment.',
    action: 'Try again',
  },
  'Session expired. Please login again.': {
    title: 'Session Expired',
    description: 'Your session has expired for security. Please sign in again to continue.',
    action: 'Sign in again',
  },
  'Refresh token has expired': {
    title: 'Session Expired',
    description: 'Your session has expired for security. Please sign in again to continue.',
    action: 'Sign in again',
  },
  'No refresh token available': {
    title: 'Session Expired',
    description: 'Your session has expired. Please sign in again to continue.',
    action: 'Sign in again',
  },
  'Bad request. Please check input values.': {
    title: 'Invalid Input',
    description: 'Some information you entered is not valid. Please review and correct it.',
    action: 'Review your input',
  },
  'Request failed': {
    title: 'Request Failed',
    description: 'Your request could not be completed. Please try again.',
    action: 'Try again',
  },
};

// Pattern-based matches (checked second, in order)
const PATTERN_MESSAGES: Array<{ pattern: RegExp; message: FriendlyError }> = [
  {
    pattern: /unauthorized|unauthenticated|401/i,
    message: {
      title: 'Access Denied',
      description: 'You are not authorized to perform this action. Please sign in or contact your administrator.',
    },
  },
  {
    pattern: /forbidden|403/i,
    message: {
      title: 'Permission Denied',
      description: 'You do not have permission to access this resource. Contact your administrator if this is unexpected.',
    },
  },
  {
    pattern: /not found|404/i,
    message: {
      title: 'Not Found',
      description: 'The requested information could not be found. It may have been moved or deleted.',
    },
  },
  {
    pattern: /conflict|already exists|duplicate/i,
    message: {
      title: 'Duplicate Entry',
      description: 'This record already exists. Please check for duplicates and try again.',
    },
  },
  {
    pattern: /validation|invalid|required field/i,
    message: {
      title: 'Validation Error',
      description: 'Some required information is missing or invalid. Please review the form and try again.',
      action: 'Review your input',
    },
  },
  {
    pattern: /timeout|timed out/i,
    message: {
      title: 'Request Timed Out',
      description: 'The server is taking too long to respond. Please try again.',
      action: 'Try again',
    },
  },
  {
    pattern: /service unavailable|503|502|bad gateway/i,
    message: {
      title: 'Service Unavailable',
      description: 'Our service is temporarily unavailable. We are working to restore it. Please try again shortly.',
      action: 'Try again later',
    },
  },
  {
    pattern: /internal server error|500/i,
    message: {
      title: 'Something Went Wrong',
      description: 'An unexpected error occurred on our end. Our team has been notified. Please try again.',
    },
  },
  {
    pattern: /network|connection|internet/i,
    message: {
      title: 'Connection Problem',
      description: 'Unable to reach the server. Please check your internet connection and try again.',
      action: 'Check your connection',
    },
  },
  {
    pattern: /fee|payment|transaction/i,
    message: {
      title: 'Payment Error',
      description: 'There was an issue processing the fee information. Please try again or contact support.',
    },
  },
  {
    pattern: /storage|quota|disk/i,
    message: {
      title: 'Storage Error',
      description: 'There was an issue saving your data. Please try again or contact support.',
    },
  },
];

const FALLBACK_MESSAGE: FriendlyError = {
  title: 'Something Went Wrong',
  description: 'An unexpected error occurred. Please try again. If the problem persists, contact support.',
  action: 'Try again',
};

/**
 * Converts a raw technical error message to a customer-friendly error object.
 */
export function getFriendlyError(rawMessage: string): FriendlyError {
  if (!rawMessage) return FALLBACK_MESSAGE;

  // Exact match
  if (EXACT_MESSAGES[rawMessage]) {
    return EXACT_MESSAGES[rawMessage];
  }

  // Pattern match
  for (const { pattern, message } of PATTERN_MESSAGES) {
    if (pattern.test(rawMessage)) {
      return message;
    }
  }

  return FALLBACK_MESSAGE;
}

/**
 * Returns a combined "Title: Description" string for compact display.
 */
export function getFriendlyMessage(rawMessage: string): string {
  const { title, description } = getFriendlyError(rawMessage);
  return `${title}: ${description}`;
}

/**
 * Returns just the friendly title for short display contexts.
 */
export function getFriendlyTitle(rawMessage: string): string {
  return getFriendlyError(rawMessage).title;
}

/**
 * Shows a react-hot-toast error with a customer-friendly message.
 * When the error is an ApiValidationError the backend messages are shown directly.
 * Use this instead of toast.error(error.message) in hooks and components.
 *
 * @example
 * mutate(data, { onError: (err) => showFriendlyErrorToast(err) })
 */
export function showFriendlyErrorToast(error: Error | string, options?: { duration?: number }): void {
  if (error instanceof ApiValidationError) {
    // Show all backend messages joined — they are already user-friendly
    const text = error.messages.join(' • ');
    toast.error(text, {
      duration: options?.duration ?? 5000,
      position: 'top-center',
    });
    return;
  }

  const rawMessage = typeof error === 'string' ? error : error.message;
  const { title, description } = getFriendlyError(rawMessage);
  toast.error(`${title} — ${description}`, {
    duration: options?.duration ?? 4000,
    position: 'top-center',
  });
}

/**
 * Adds a persistent on-screen error card (visible until dismissed).
 * Users can screenshot it and share the reference ID with support.
 *
 * When the error is an ApiValidationError the backend messages are shown
 * verbatim — they are already written for end users and must not be mapped
 * to a generic fallback.
 *
 * For all other errors the raw message is translated through getFriendlyError()
 * so technical noise never reaches the user.
 *
 * The reference ID is logged to Sentry (as a tag) and Azure App Insights
 * (as a trace property) so the support team can search by it.
 *
 * Search in Sentry:       tag: referenceId = "BKBAE85CC"
 * Search in App Insights: traces | where customDimensions.referenceId == "BKBAE85CC"
 *
 * Returns the reference ID.
 */
export function showPersistentError(error: Error | string): string {
  // For SystemErrors, reuse the referenceId generated at request time —
  // the same ID was sent as X-Reference-Id so backend logs carry it.
  // For all other errors (e.g. thrown directly in components), generate a new one.
  const referenceId =
    error instanceof SystemError && error.referenceId
      ? error.referenceId
      : generateReferenceId();

  // ── Backend validation errors: show messages verbatim ─────────────────────
  if (error instanceof ApiValidationError) {
    const title = 'Validation Error';
    const [firstMessage = 'Please review your input.'] = error.messages;

    captureMessage(
      `[${referenceId}] ${title}: ${error.messages.join(' | ')}`,
      'warning',
      { referenceId, friendlyTitle: title, rawMessage: error.messages.join(' | ') },
      { referenceId }
    );

    trackAppTrace(
      `[${referenceId}] ${title}: ${error.messages.join(' | ')}`,
      SeverityLevel.Warning,
      { referenceId, friendlyTitle: title, rawMessage: error.messages.join(' | ') }
    );

    useErrorStore.getState().addErrorWithId(
      referenceId,
      title,
      firstMessage,
      'Review your input',
      error.messages,
      true
    );

    return referenceId;
  }

  // ── System errors: translate through getFriendlyError(), surface trace IDs ─
  const rawMessage = typeof error === 'string' ? error : error.message;
  const { title, description, action } = getFriendlyError(rawMessage);

  // Pull correlation IDs from SystemError so they reach the ErrorPanel and
  // get logged to Sentry + App Insights for cross-system searching.
  const requestId = error instanceof SystemError ? error.requestId : undefined;
  const backendTraceId = error instanceof SystemError ? error.backendTraceId : undefined;

  captureMessage(
    `[${referenceId}] ${title}: ${rawMessage}`,
    'error',
    {
      referenceId, friendlyTitle: title, rawMessage,
      ...(requestId ? { requestId } : {}),
      ...(backendTraceId ? { backendTraceId } : {}),
    },
    {
      referenceId,
      ...(requestId ? { requestId } : {}),
      ...(backendTraceId ? { backendTraceId } : {}),
    }
  );

  trackAppTrace(
    `[${referenceId}] ${title}: ${rawMessage}`,
    SeverityLevel.Error,
    {
      referenceId, friendlyTitle: title, rawMessage,
      ...(requestId ? { requestId } : {}),
      ...(backendTraceId ? { backendTraceId } : {}),
    }
  );

  useErrorStore.getState().addErrorWithId(
    referenceId, title, description, action,
    undefined, false,
    requestId, backendTraceId
  );

  return referenceId;
}
